import { Box, CheckSquare, Edit, Eye, History, Package, Plus, Square, FileSpreadsheet, FileText, Image, Ticket } from "lucide-react";
import { FC, useMemo, useState } from "react";
import { capitalize, formatDimensions, formatPackingType } from "~/utils";
import { useDetailBookingStore } from "../detail-booking.store";
import {
  InventoryStatusCode,
  InventoryType,
  SortOrder,
} from "~/utils/constants";
import { TInventory } from "~/types/inventory";
import { Carousel, message } from "antd";
import "./inventory.css";
import { useInventoryStore } from "~/components/admin/inventories";
import { SortByField } from "~/services/inventory";
import InventoryHistoryModal from "./inventory-history-modal";
import { PrintLabels } from "~/components/admin/dispatch/print-labels/print-label";
import { InventoryBulkUpdateButton } from "./inventory-bulk-update-button";
import { InventoryManageTicketsModal } from "./inventory-manage-tickets-modal";
import { ConvertTemplatesModal } from "./convert-templates-modal";
import { ExportModal, ExportType } from "~/components/client/inventory/export-modal";
import { exportToCSV, exportToPDF, exportPhotoList } from "~/components/client/inventory/export-utils";



type Props = {};

export const Inventory: FC<Props> = () => {
  const { booking, selectedInventory, setSelectedInventory } =
    useDetailBookingStore();
  const { openInventoryModal } = useInventoryStore();
  const [sortBy, setSortBy] = useState<SortByField | string>(SortByField.INVENTORY_REF);
  const [sortOrder, setSortOrder] = useState<SortOrder>(SortOrder.ASC);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [historyInventory, setHistoryInventory] = useState<TInventory | null>(null);
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [isManageTicketsModalOpen, setIsManageTicketsModalOpen] = useState(false);
  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [exportType, setExportType] = useState<ExportType>("csv");

 const handleAddInventory = () => {
  // Open the inventory form modal for creating new inventory
  openInventoryModal();
};

  const handleSelectInventory = (inventory: TInventory) => {
    setSelectedInventory(inventory);
  };

  const handleOpenHistory = (inventory: TInventory) => {
    setHistoryInventory(inventory);
    setHistoryModalOpen(true);
  };

  const handleCloseHistory = () => {
    setHistoryModalOpen(false);
    setHistoryInventory(null);
  };

  const handleSort = (field: SortByField | string) => {
    if (sortBy === field) {
      setSortOrder(
        sortOrder === SortOrder.ASC ? SortOrder.DESC : SortOrder.ASC
      );
    } else {
      setSortBy(field);
      setSortOrder(SortOrder.ASC);
    }
  };

  const toggleSelection = (id: string) => {
    setSelectedItemIds((prev) =>
      prev.includes(id) ? prev.filter((itemId) => itemId !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    const allIds = allInventories.map((item) => item.id);
    if (selectedItemIds.length === allIds.length) {
      setSelectedItemIds([]);
    } else {
      setSelectedItemIds(allIds);
    }
  };

  const allInventories = useMemo(() => {
    if (!booking) return [];

    const uniqueInventories = new Map();

    // Collect all unique inventories from service tickets
    // No client filtering needed - inventories are already linked via service tickets to this booking
    booking.serviceTicket.forEach((ticket) => {
      (ticket.inventories ?? []).forEach((inventory) => {
        if (!uniqueInventories.has(inventory.id)) {
          uniqueInventories.set(inventory.id, inventory);
        }
      });
    });

    const inventories = Array.from(uniqueInventories.values());

    return inventories.sort((a, b) => {
      const aValue = a[sortBy] || "";
      const bValue = b[sortBy] || "";
      const compare = aValue.localeCompare(bValue, undefined, {
        sensitivity: "base",
      });
      return sortOrder === SortOrder.ASC ? compare : -compare;
    });
  }, [booking, sortBy, sortOrder]);

  const selectedInventories = useMemo(() => {
    return allInventories
      .filter((item) => selectedItemIds.includes(item.id))
      .map((inventory) => ({
        ...inventory,
        // Ensure client data is available for printing
        client: inventory.client || booking?.client,
      }));
  }, [allInventories, selectedItemIds, booking]);

  // Get inventories for export (selected items or all items)
  const inventoriesToExport = useMemo(() => {
    if (selectedItemIds.length > 0) {
      return selectedInventories;
    }
    // If no items selected, export all items
    return allInventories.map((inventory) => ({
      ...inventory,
      // Ensure client data is available
      client: inventory.client || booking?.client,
    }));
  }, [selectedItemIds, selectedInventories, allInventories, booking]);

  const handleExportClick = (type: ExportType) => {
    if (inventoriesToExport.length === 0) {
      message.warning("No items to export. Please select items or ensure there are items in the list.");
      return;
    }
    setExportType(type);
    setIsExportModalOpen(true);
  };

  const handleExport = (columns: string[], type: ExportType, action: "view" | "download") => {
    try {
      switch (type) {
        case "csv":
          exportToCSV(inventoriesToExport, columns, action);
          break;
        case "pdf":
          exportToPDF(inventoriesToExport, columns, action);
          break;
        case "photo-list":
          exportPhotoList(inventoriesToExport, columns, action);
          break;
      }
      setIsExportModalOpen(false);
      message.success(`Export ${action === "view" ? "opened" : "downloaded"} successfully`);
    } catch (error) {
      console.error("Export error:", error);
      message.error("Failed to export. Please try again.");
    }
  };

  const getInventoryIcon = (itemType?: InventoryType) => {
    switch (itemType) {
      case InventoryType.PACKAGE:
        return <Box size={18} className="text-amber-500" />;
      default:
        return <Package size={18} className="text-green-500" />;
    }
  };

  const getStatusColor = (status?: InventoryStatusCode) => {
    switch (status) {
      case InventoryStatusCode.AVAILABLE_LIVE:
        return "bg-green-100 text-green-800";
      case InventoryStatusCode.AVAILABLE_ON_HAND:
        return "bg-blue-100 text-blue-800";
      case InventoryStatusCode.NO_POSSESSION:
        return "bg-orange-100 text-orange-800";
      case InventoryStatusCode.RELEASED:
        return "bg-purple-100 text-purple-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  if (!booking) {
    return null;
  }

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium">Inventory</h3>
        <div className="flex items-center space-x-3">
          {selectedItemIds.length > 0 && (
            <span className="text-sm text-gray-600">
              {selectedItemIds.length} selected
            </span>
          )}
          {allInventories.length > 0 && (
            <>
              <button
                onClick={() => handleExportClick("csv")}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-1"
                title="Export inventory as CSV"
              >
                <FileSpreadsheet size={16} />
                <span>Export CSV</span>
              </button>
              <button
                onClick={() => handleExportClick("pdf")}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-1"
                title="Export inventory as PDF"
              >
                <FileText size={16} />
                <span>Export PDF</span>
              </button>
              <button
                onClick={() => handleExportClick("photo-list")}
                className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-1"
                title="Export inventory as photo list"
              >
                <Image size={16} />
                <span>Photo List</span>
              </button>
            </>
          )}
          <button
            onClick={() => setConvertModalOpen(true)}
            className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center space-x-1 text-gray-700"
            title="Convert estimate templates to inventory items"
          >
            <FileText size={16} />
            <span>Convert Templates</span>
          </button>
          <InventoryBulkUpdateButton
            selectedInventories={selectedInventories}
            onUpdateComplete={() => setSelectedItemIds([])}
          />
          <button
            onClick={() => setIsManageTicketsModalOpen(true)}
            disabled={selectedInventories.length === 0}
            className={`px-3 py-2 rounded-lg flex items-center space-x-1 ${
              selectedInventories.length === 0
                ? "border border-gray-200 text-gray-400 cursor-not-allowed"
                : "border border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
            title="Add or remove selected inventory from service tickets"
          >
            <Ticket size={16} />
            <span>Manage Tickets</span>
          </button>
          <PrintLabels selectedInventories={selectedInventories} />
          <button
            onClick={handleAddInventory}
            className="px-3 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 flex items-center space-x-1"
            title="Creates new inventory for client, does not auto add to this list"
          >
            <Plus size={16} />
            <span>Create inventory</span>
          </button>
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 text-left text-xs text-gray-500">
            <tr>
              <th className="px-4 py-3 w-10">
                <button
                  onClick={toggleSelectAll}
                  className="flex items-center justify-center"
                >
                  {selectedItemIds.length === allInventories.length &&
                  allInventories.length > 0 ? (
                    <CheckSquare size={16} className="text-blue-500" />
                  ) : (
                    <Square size={16} className="text-gray-400" />
                  )}
                </button>
              </th>
              <th
                className="px-4 py-3 cursor-pointer hover:text-blue-600"
                onClick={() => handleSort(SortByField.INVENTORY_REF)}
              >
                <div className="flex items-center">
                  <span>ID</span>
                  <span className="ml-2 text-xs">
                    <span
                      className={
                        sortBy === SortByField.INVENTORY_REF &&
                        sortOrder === SortOrder.ASC
                          ? "text-blue-600"
                          : "text-gray-400"
                      }
                    >
                      ↑
                    </span>
                    <span
                      className={
                        sortBy === SortByField.INVENTORY_REF &&
                        sortOrder === SortOrder.DESC
                          ? "text-blue-600"
                          : "text-gray-400"
                      }
                    >
                      ↓
                    </span>
                  </span>
                </div>
              </th>
               <th
                className="px-4 py-3 cursor-pointer hover:text-blue-600"
                onClick={() => handleSort("clientInternalRef")}
              >
                <div className="flex items-center">
                  <span>Client Ref #</span>
                  <span className="ml-2 text-xs">
                    <span
                      className={
                        sortBy === "clientInternalRef" &&
                        sortOrder === SortOrder.ASC
                          ? "text-blue-600"
                          : "text-gray-400"
                      }
                    >
                      ↑
                    </span>
                    <span
                      className={
                        sortBy === "clientInternalRef" &&
                        sortOrder === SortOrder.DESC
                          ? "text-blue-600"
                          : "text-gray-400"
                      }
                    >
                      ↓
                    </span>
                  </span>
                </div>
              </th>
              <th
                className="px-4 py-3 cursor-pointer hover:text-blue-600"
                onClick={() => handleSort(SortByField.TITLE)}
              >
                <div className="flex items-center">
                  <span>Title</span>
                  <span className="ml-2 text-xs">
                    <span
                      className={
                        sortBy === SortByField.TITLE &&
                        sortOrder === SortOrder.ASC
                          ? "text-blue-600"
                          : "text-gray-400"
                      }
                    >
                      ↑
                    </span>
                    <span
                      className={
                        sortBy === SortByField.TITLE &&
                        sortOrder === SortOrder.DESC
                          ? "text-blue-600"
                          : "text-gray-400"
                      }
                    >
                      ↓
                    </span>
                  </span>
                </div>
              </th>
              <th
                className="px-4 py-3 cursor-pointer hover:text-blue-600"
                onClick={() => handleSort(SortByField.ARTIST_NAME)}
              >
                <div className="flex items-center">
                  <span>Artist</span>
                  <span className="ml-2 text-xs">
                    <span
                      className={
                        sortBy === SortByField.ARTIST_NAME &&
                        sortOrder === SortOrder.ASC
                          ? "text-blue-600"
                          : "text-gray-400"
                      }
                    >
                      ↑
                    </span>
                    <span
                      className={
                        sortBy === SortByField.ARTIST_NAME &&
                        sortOrder === SortOrder.DESC
                          ? "text-blue-600"
                          : "text-gray-400"
                      }
                    >
                      ↓
                    </span>
                  </span>
                </div>
              </th>
              <th className="px-4 py-3">Medium</th>
              <th className="px-4 py-3">Packing</th>
              <th className="px-4 py-3">Dimensions</th>
              <th className="px-4 py-3">Value</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Location</th>
              <th className="px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y">
            {allInventories.map((item) => (
              <tr
                key={item.id}
                className={`hover:bg-gray-50 cursor-pointer ${
                  selectedInventory?.id === item.id ? "bg-blue-50" : ""
                }`}
                onClick={() => handleSelectInventory(item)}
              >
                <td className="px-4 py-3 text-sm">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleSelection(item.id);
                    }}
                    className="flex items-center justify-center"
                  >
                    {selectedItemIds.includes(item.id) ? (
                      <CheckSquare size={16} className="text-blue-500" />
                    ) : (
                      <Square size={16} className="text-gray-400" />
                    )}
                  </button>
                </td>
                <td className="px-4 py-3 text-sm">{item.inventoryRef}</td>
                <td className="px-4 py-3 font-medium">{item.clientInternalRef}</td>
                <td className="px-4 py-3 font-medium">{item.title}</td>
                <td className="px-4 py-3 text-sm">{item.artistName}</td>
                <td className="px-4 py-3 text-sm">{item.medium}</td>
                <td className="px-4 py-3 text-sm">{formatPackingType(item)}</td>
                <td className="px-4 py-3 text-sm">{formatDimensions(item)}</td>
                <td className="px-4 py-3 text-sm">
                  ${item.value?.toLocaleString()}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${getStatusColor(
                      item.status
                    )}`}
                  >
                    {capitalize(item.status)}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">{item.location?.name}</td>
                <td className="px-4 py-3">
                  <div className="flex space-x-1">
                    <button
                      className="p-1 text-gray-400 hover:text-blue-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSelectInventory(item);
                      }}
                      title="View details"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      className="p-1 text-gray-400 hover:text-blue-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        openInventoryModal(item.id);
                      }}
                      title="Edit inventory"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className="p-1 text-gray-400 hover:text-blue-600"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleOpenHistory(item);
                      }}
                      title="View history"
                    >
                      <History size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {allInventories.length === 0 && (
              <tr>
                <td colSpan={11} className="px-4 py-6 text-center text-gray-500">
                  No inventory items found for this booking
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6">
        <h3 className="text-lg font-medium mb-4">Inventory Details</h3>
        <div className="bg-gray-50 border rounded-lg p-4">
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-1">
              <div className="bg-white border rounded-lg p-4 h-64">
                {selectedInventory &&
                selectedInventory.images &&
                selectedInventory.images.length > 0 ? (
                  <div className="relative h-56 inventory-carousel">
                    <Carousel
                      autoplay
                      autoplaySpeed={3000}
                      className="h-56"
                      arrows
                    >
                      {selectedInventory.images.map((image, index) => (
                        <div
                          key={image.id || index}
                          className="h-56 flex items-center justify-center"
                        >
                          <img
                            src={image.url}
                            alt={`${selectedInventory.title} - Image ${
                              index + 1
                            }`}
                            className="max-w-full max-h-56 object-contain object-center"
                          />
                        </div>
                      ))}
                    </Carousel>
                  </div>
                ) : (
                  <div className="h-full flex items-center justify-center text-center text-gray-400">
                    <div>
                      <Package size={48} className="mx-auto mb-2" />
                      <p>No preview available</p>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="col-span-2">
              <div className="bg-white border rounded-lg p-4 h-64 overflow-auto">
                {selectedInventory ? (
                  <div>
                    <div className="flex justify-between items-start">
                      <h4 className="font-medium text-lg mb-3">
                        {selectedInventory.title || "Untitled Item"}
                      </h4>
                      <span
                        className={`px-2 py-1 rounded-full text-xs ${getStatusColor(
                          selectedInventory.status
                        )}`}
                      >
                        {capitalize(selectedInventory.status)}
                      </span>
                    </div>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <div className="flex items-center mb-2">
                          {getInventoryIcon(selectedInventory.itemType)}
                          <span className="font-medium ml-2">
                            {capitalize(selectedInventory.itemType)}
                          </span>
                        </div>
                        <p className="text-gray-600 mb-1">
                          <span className="font-medium">Artist:</span>{" "}
                          {selectedInventory.artistName || "Not specified"}
                        </p>
                        <p className="text-gray-600 mb-1">
                          <span className="font-medium">Ref ID:</span>{" "}
                          {selectedInventory.clientInternalRef || "Not assigned"}
                        </p>
                        <p className="text-gray-600 mb-1">
                          <span className="font-medium">Medium:</span>{" "}
                          {selectedInventory.medium || "Not specified"}
                        </p>
                        <p className="text-gray-600 mb-1">
                          <span className="font-medium">Dimensions:</span>{" "}
                          {formatDimensions(selectedInventory)}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-600 mb-1">
                          <span className="font-medium">Value:</span> $
                          {selectedInventory.value?.toLocaleString() ||
                            "Not specified"}
                        </p>
                        <p className="text-gray-600 mb-1">
                          <span className="font-medium">Packing:</span>{" "}
                          {formatPackingType(selectedInventory)}
                        </p>
                        <p className="text-gray-600 mb-1">
                          <span className="font-medium">Location:</span>{" "}
                          {selectedInventory.location?.name || "Not specified"}
                        </p>
                        <p className="text-gray-600 mb-1">
                          <span className="font-medium">Parts:</span>{" "}
                          {selectedInventory.parts || 1}
                        </p>
                        <p className="text-gray-600 mb-1">
                          <span className="font-medium">Weight:</span>{" "}
                          {selectedInventory.weight
                            ? `${selectedInventory.weight} lbs`
                            : "Not specified"}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <h4 className="font-medium mb-3">
                      Select an item to view details
                    </h4>
                    <p className="text-gray-500">
                      Click on any inventory item to see detailed information
                      including condition reports, handling instructions, and
                      movement history.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <InventoryHistoryModal
        inventory={historyInventory}
        open={historyModalOpen}
        onClose={handleCloseHistory}
      />

      <ConvertTemplatesModal
        open={convertModalOpen}
        onClose={() => setConvertModalOpen(false)}
        serviceTickets={booking.serviceTicket || []}
        clientId={booking.clientId || booking.client?.id || ""}
        bookingId={booking.id}
      />

      {/* Export Inventory Modal */}
      <ExportModal
        open={isExportModalOpen}
        onClose={() => setIsExportModalOpen(false)}
        exportType={exportType}
        inventories={inventoriesToExport}
        onExport={handleExport}
      />

      {/* Manage Tickets Modal */}
      <InventoryManageTicketsModal
        open={isManageTicketsModalOpen}
        onClose={() => setIsManageTicketsModalOpen(false)}
        selectedInventories={selectedInventories}
        serviceTickets={booking.serviceTicket || []}
        onSuccess={() => {
          setIsManageTicketsModalOpen(false);
          setSelectedItemIds([]);
        }}
      />
    </div>
  );
};
